import Decimal from 'decimal.js';

/**
 * Tax (IVA) rate constants — single source of truth for the FE/BE.
 *
 * Two distinct rates because they cover two different surfaces:
 *
 *   - `DEFAULT_TAX_RATE` (19%) — Slice 8 standalone calculator
 *     (`apps/web/src/features/calculator/...`) + the BE parity mirror
 *     (`POST /api/v1/simulate/quote`). Locked at 19% by the
 *     calculator spec (obs-444).
 *
 *   - `IVA_RATE` (16%) — Venezuela IVA on customer-facing
 *     `QuotationForm` (sales quotations) and the FE→BE payload it
 *     produces. Hardcoded at 16% per the business decision to align
 *     the sales module with the Venezuelan tax rate (path C from the
 *     tax-rate reconciliation: replace the historical 22% constant
 *     with the live rate rather than per-tenant configurability).
 *
 * The two rates are intentionally separate: the calculator is a
 * neutral simulator; the quotation form is a Venezuelan commercial
 * document. Collapsing them into one constant would require the
 * calculator to also render 16%, which is not the Slice 8 contract.
 *
 * Why `Decimal('0.16')` / `Decimal('0.19')` and not the number literals:
 *   `0.16` in IEEE 754 is `0.15999999999999998...` so `0.16 * 100
 *   === 15.999999999999998`. Money math needs decimal.js to avoid
 *   silent rounding drift; the rest of `calculator-math.ts` and
 *   `apps/api/src/routes/simulate.ts` already follow that discipline.
 *   Constructing `new Decimal('0.16')` from the literal string keeps
 *   the rate exact and round-trippable.
 *
 * Why these are exported as named constants (not an enum / union):
 *   - They are read once per quote computation; V8 inlines the value.
 *   - Keeping them as `const` (frozen literals) preserves the same
 *     call-site ergonomics as the original `0.22` / `0.19` literals.
 *   - A future "configurable rate per tenant" feature would replace
 *     `IVA_RATE` with a DB-backed lookup; the call sites would change
 *     once, here, instead of in every file that hard-codes the rate.
 */

/**
 * Default IVA rate (19%) — Slice 8 calculator + BE parity.
 *
 * Consumers:
 *   - `apps/web/src/features/calculator/lib/calculator-math.ts`
 *   - `apps/api/src/routes/simulate.ts` (BE parity mirror)
 */
export const DEFAULT_TAX_RATE = new Decimal('0.19');

/**
 * Venezuelan IVA rate (16%) — sales quotation form + payload.
 *
 * Consumers:
 *   - `apps/web/src/features/sales/components/QuotationForm.tsx`
 *   - `apps/web/src/features/sales/api/quotations.ts` (FE → BE payload)
 */
export const IVA_RATE = new Decimal('0.16');
